/**
 * Dados da Central de Projetos para a ficha do cliente (só com
 * `central_projetos:view` e a Central ligada): a leitura do Analista e as
 * conversas do pedido com a conversa mais recente, e as pendências abertas de
 * TODOS os pedidos do cliente. Sem permissão, devolve `ficha: null`.
 */
import { useCallback, useEffect, useState } from "react";

import { MODULO_CENTRAL_PROJETOS } from "../../../../config/modulos";
import { useAuth } from "../../../../context/AuthContext";
import { lerFicha, listarPendencias } from "../../../../services/centralProjetos";
import { PERMISSAO_CENTRAL } from "./useCentralDaCarteira";

const PERMISSAO_TRATAR = "central_projetos:tratar";

/** O pedido com a mensagem mais recente é o "principal" da ficha da Central. */
export function pedidoPrincipal(numeros, carteira) {
  if (!numeros.length) return null;
  const comMensagem = numeros
    .map((n) => ({ n, em: carteira?.[n]?.ultima_mensagem_em ? Date.parse(carteira[n].ultima_mensagem_em) : 0 }))
    .sort((a, b) => b.em - a.em);
  return comMensagem[0].em ? comMensagem[0].n : numeros[0];
}

export default function useCentralDaFicha(numerosDosPedidos, carteira) {
  const { request, hasPermission, temModulo } = useAuth();
  // Central só com o módulo contratado (ADR-0102); sem ele a lista fica só com a planilha.
  const podeVer = Boolean(hasPermission?.(PERMISSAO_CENTRAL)) && Boolean(temModulo?.(MODULO_CENTRAL_PROJETOS));
  const podeTratar = Boolean(hasPermission?.(PERMISSAO_TRATAR));
  const principal = pedidoPrincipal(numerosDosPedidos, carteira);
  const chave = numerosDosPedidos.join("|");
  const [estado, setEstado] = useState({ ficha: null, pendencias: [], carregando: podeVer });
  const [versao, setVersao] = useState(0);
  const recarregar = useCallback(() => setVersao((v) => v + 1), []);

  useEffect(() => {
    if (!podeVer || !principal) {
      setEstado({ ficha: null, pendencias: [], carregando: false });
      return undefined;
    }
    let vivo = true;
    const numeros = chave.split("|").filter(Boolean);
    Promise.all([
      lerFicha(request, principal),
      Promise.all(numeros.map((n) => listarPendencias(request, { cliente: n }).catch(() => ({ pendencias: [] })))),
    ])
      .then(([ficha, porPedido]) => {
        if (!vivo) return;
        const pendencias = porPedido.flatMap((r) => r?.pendencias || []);
        setEstado({ ficha, pendencias, carregando: false });
      })
      .catch(() => {
        // Central desligada (409) ou pedido fora do Gerenciador (404): a ficha segue só com a planilha.
        if (vivo) setEstado({ ficha: null, pendencias: [], carregando: false });
      });
    return () => {
      vivo = false;
    };
  }, [podeVer, principal, chave, request, versao]);

  return { ...estado, podeVer, podeTratar, recarregar };
}
